import type { PipelineStatus } from "@Simplicity-Education/core";
import { Button, CategoryBadge, type CategoryColor } from "@simplicity/ui";
import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  ReactFlowProvider,
  type Edge,
  type Node,
  type NodeMouseHandler,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PipelineGraph, Wave } from "../../lib/buildPipelineGraph";
import {
  buildFlowElements,
  computeCascade,
  DEFAULT_COLLAPSED_WAVES,
  selectionToNodeId,
  type CombinedBronzeSilverData,
  type TableNodeData,
  type WaveGroupData,
} from "./pipelineDagBuilder";

// Window inside which a second mousedown on the same node is treated as a
// double-click (drawer open) and cancels the pending single-click focus.
const DOUBLE_CLICK_MS = 250;

// SIM-1350 follow-up: color-coded cadence badge. Color map matches the
// config app's TablesEditor so a cadence reads the same across surfaces.
// Source: apps/web-admin/src/components/config/tables/TablesEditor.tsx.
const CADENCE_COLORS: Record<string, CategoryColor> = {
  hourly: "amber",
  daily: "emerald",
  weekly: "violet",
  monthly: "blue",
};

function cadenceColor(cadence: string | undefined): CategoryColor {
  if (cadence == null) return "slate";
  return CADENCE_COLORS[cadence.toLowerCase()] ?? "slate";
}

/**
 * SIM-2913 slice C: a Record rather than a switch so widening the shared enum
 * breaks the build here instead of silently falling through to no colour.
 * `paused` and `unscheduled` are muted on purpose — neither is a problem, and
 * colouring them amber trains people to ignore amber.
 */
const STATUS_DOT_CLASS: Record<PipelineStatus, string> = {
  fresh: "bg-success",
  running: "bg-success animate-pulse",
  late: "bg-warning",
  empty: "bg-warning",
  suspect: "bg-warning",
  failed: "bg-destructive",
  never: "bg-muted-foreground",
  unknown: "bg-muted-foreground",
  paused: "bg-muted-foreground",
  unscheduled: "bg-muted-foreground",
};

function statusDotClass(status: PipelineStatus): string {
  return STATUS_DOT_CLASS[status];
}

function traceClass(inTrace: boolean | undefined): string {
  if (inTrace === undefined) return "";
  return inTrace ? "ring-2 ring-primary" : "opacity-30";
}

function cadenceFilterClass(matchesFilter: boolean | undefined): string {
  // Independent of trace — both dimming layers compose multiplicatively at the
  // visual layer via Tailwind's opacity-* utilities.
  if (matchesFilter !== false) return "";
  return "opacity-30";
}

function formatRelativeTime(epochMs: number | null): string {
  if (epochMs == null) return "—";
  const deltaMs = Date.now() - epochMs;
  if (deltaMs < 0) return "just now";
  const min = Math.floor(deltaMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

interface DagCallbacks {
  toggleShowInactive: (wave: Wave) => void;
}

const DagCallbacksContext = createContext<DagCallbacks | null>(null);

function useDagCallbacks(): DagCallbacks {
  const ctx = useContext(DagCallbacksContext);
  if (ctx == null) throw new Error("DagCallbacksContext provider missing");
  return ctx;
}

function WaveGroupNode({
  data,
}: {
  data: WaveGroupData & { inTrace?: boolean; matchesFilter?: boolean };
}) {
  const { toggleShowInactive } = useDagCallbacks();
  const hiddenInactive = data.totalCount - data.activeCount;
  const showInactiveAvailable = hiddenInactive > 0;
  return (
    <div
      className={`flex h-full w-full flex-col rounded-lg border border-border bg-muted/30 shadow-sm transition ${traceClass(data.inTrace)} ${cadenceFilterClass(data.matchesFilter)}`}
      data-wave={data.wave}
      data-kind="wave-group"
      data-status={data.status}
      data-expanded={data.expanded ? "true" : "false"}
      data-in-trace={data.inTrace === undefined ? undefined : data.inTrace ? "true" : "false"}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} />
      <div className="flex w-full items-center justify-between gap-2 px-4 py-3">
        <span className="flex items-center gap-2">
          <span aria-hidden="true" className="text-xs text-muted-foreground">
            {data.expanded ? "▾" : "▸"}
          </span>
          <span className="text-sm font-medium text-foreground">{data.label}</span>
          <span className="text-xs text-muted-foreground">
            ({data.totalCount} {data.totalCount === 1 ? "item" : "items"}
            {showInactiveAvailable ? `, ${data.activeCount} active` : ""})
          </span>
        </span>
        <span className="flex items-center gap-2">
          {showInactiveAvailable && data.expanded ? (
            <Button
              variant="chip"
              data-action="toggle-inactive"
              aria-pressed={data.showInactive}
              onClick={(e) => {
                e.stopPropagation();
                toggleShowInactive(data.wave);
              }}
            >
              {data.showInactive ? "Hide inactive" : `Show ${hiddenInactive} inactive`}
            </Button>
          ) : null}
          <span
            aria-hidden="true"
            className={`h-2.5 w-2.5 rounded-full ${statusDotClass(data.status)}`}
          />
        </span>
      </div>
      {/* The aria-expanded button is the click target for group expansion. */}
      <button
        type="button"
        className="sr-only"
        aria-expanded={data.expanded}
        aria-label={`${data.label} wave, ${data.totalCount} ${data.totalCount === 1 ? "item" : "items"}, status ${data.status}, ${data.expanded ? "expanded — click to collapse" : "collapsed — click to expand"}`}
        tabIndex={-1}
      />
      <Handle type="source" position={Position.Right} isConnectable={false} />
    </div>
  );
}

function CadenceChip({ cadence }: { cadence: string }) {
  return (
    <CategoryBadge color={cadenceColor(cadence)} data-cadence={cadence}>
      {cadence.charAt(0).toUpperCase() + cadence.slice(1)}
    </CategoryBadge>
  );
}

function CadenceList({ cadences }: { cadences: string[] }) {
  if (cadences.length === 0) {
    return <span className="text-[11px] text-muted-foreground">—</span>;
  }
  return (
    <span className="flex flex-wrap items-center gap-1">
      {cadences.map((c) => (
        <CadenceChip key={c} cadence={c} />
      ))}
    </span>
  );
}

function TableCardNode({
  data,
}: {
  data: TableNodeData & { inTrace?: boolean; matchesFilter?: boolean; active?: boolean };
}) {
  const inactiveClass = data.active === false ? "opacity-60 grayscale" : "";
  return (
    <div
      className={`flex h-full w-full flex-col justify-center gap-1 rounded border border-border bg-card px-3 py-2 shadow-sm transition ${traceClass(data.inTrace)} ${cadenceFilterClass(data.matchesFilter)} ${inactiveClass}`}
      data-wave={data.wave}
      data-status={data.status}
      data-kind="table"
      data-active={data.active === false ? "false" : "true"}
      data-in-trace={data.inTrace === undefined ? undefined : data.inTrace ? "true" : "false"}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} />
      <div className="flex items-center justify-between gap-2">
        <span
          className="truncate text-sm text-foreground"
          title={data.label}
          aria-label={data.label}
        >
          {data.label}
        </span>
        <span
          aria-label={`Status: ${data.status}`}
          className={`h-2 w-2 shrink-0 rounded-full ${statusDotClass(data.status)}`}
        />
      </div>
      <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
        <CadenceList cadences={data.cadences} />
        <span>{data.rowCount != null ? data.rowCount.toLocaleString() : "—"}</span>
      </div>
      <Handle type="source" position={Position.Right} isConnectable={false} />
    </div>
  );
}

function CombinedBronzeSilverNode({
  data,
}: {
  data: CombinedBronzeSilverData & {
    inTrace?: boolean;
    matchesFilter?: boolean;
    active?: boolean;
  };
}) {
  const inactiveClass = data.active === false ? "opacity-60 grayscale" : "";
  return (
    <div
      className={`flex h-full w-full flex-col gap-1 rounded border border-border bg-card px-3 py-2 shadow-sm transition ${traceClass(data.inTrace)} ${cadenceFilterClass(data.matchesFilter)} ${inactiveClass}`}
      data-wave={data.wave}
      data-status={data.status}
      data-kind="combined-bronze-silver"
      data-active={data.active === false ? "false" : "true"}
      data-in-trace={data.inTrace === undefined ? undefined : data.inTrace ? "true" : "false"}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} />
      <div className="flex items-center justify-between gap-2">
        <span
          className="truncate text-sm font-medium text-foreground"
          title={data.label}
          aria-label={data.label}
        >
          {data.label}
        </span>
        <span
          aria-label={`Status: ${data.status}`}
          className={`h-2 w-2 shrink-0 rounded-full ${statusDotClass(data.status)}`}
        />
      </div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <CadenceList cadences={data.cadences} />
        <span>{formatRelativeTime(data.lastModifiedMs)}</span>
      </div>
      <div className="text-[11px] text-muted-foreground">
        {data.numSnapshots != null ? `${data.numSnapshots.toLocaleString()} snapshots` : "—"}
        <span className="px-1.5">→</span>
        {data.numRows != null ? `${data.numRows.toLocaleString()} rows` : "—"}
      </div>
      <Handle type="source" position={Position.Right} isConnectable={false} />
    </div>
  );
}

const nodeTypes = {
  "wave-group": WaveGroupNode,
  table: TableCardNode,
  "combined-bronze-silver": CombinedBronzeSilverNode,
};

export interface TableSelection {
  wave: Wave;
  entityId: string;
}

interface PipelineDagProps {
  graph: PipelineGraph;
  /** Opens the drawer (double-click action). */
  onTableSelect?: (selection: TableSelection) => void;
  /** When set, the cascade (upstream + downstream) of this node highlights; other nodes fade. */
  focusedSelection?: TableSelection | null;
  /** Single-click focus → setter from the page that drives `focusedSelection`. */
  onFocusChange?: (selection: TableSelection | null) => void;
}

function nodeDataAsSelection(node: Node): TableSelection | null {
  const data = node.data as { kind?: string; wave?: Wave; entityId?: string };
  if (data.wave == null || data.entityId == null) return null;
  if (
    data.kind !== "table" &&
    data.kind !== "combined-bronze-silver" &&
    data.kind !== "connector"
  ) {
    return null;
  }
  return { wave: data.wave, entityId: data.entityId };
}

interface PendingClick {
  nodeId: string;
  timer: ReturnType<typeof setTimeout>;
  selection: TableSelection;
}

function useExpandedWaves(graph: PipelineGraph): {
  expandedWaves: ReadonlySet<Wave>;
  toggleWave: (wave: Wave) => void;
} {
  const [expandedWaves, setExpandedWaves] = useState<ReadonlySet<Wave>>(() => {
    const expanded = new Set<Wave>();
    for (const w of graph.visibleWaves) {
      if (!DEFAULT_COLLAPSED_WAVES.has(w)) expanded.add(w);
    }
    return expanded;
  });
  const toggleWave = useCallback((wave: Wave) => {
    setExpandedWaves((prev) => {
      const next = new Set(prev);
      if (next.has(wave)) next.delete(wave);
      else next.add(wave);
      return next;
    });
  }, []);
  return { expandedWaves, toggleWave };
}

function useShowInactiveByWave(): {
  showInactiveByWave: ReadonlySet<Wave>;
  toggleShowInactive: (wave: Wave) => void;
} {
  const [showInactiveByWave, setShowInactiveByWave] = useState<ReadonlySet<Wave>>(
    () => new Set<Wave>(),
  );
  const toggleShowInactive = useCallback((wave: Wave) => {
    setShowInactiveByWave((prev) => {
      const next = new Set(prev);
      if (next.has(wave)) next.delete(wave);
      else next.add(wave);
      return next;
    });
  }, []);
  return { showInactiveByWave, toggleShowInactive };
}

function useTraceSet(
  edges: Edge[],
  focusedSelection: TableSelection | null | undefined,
): Set<string> | null {
  return useMemo<Set<string> | null>(() => {
    if (focusedSelection == null) return null;
    const seedId = selectionToNodeId(focusedSelection);
    return computeCascade(seedId, edges);
  }, [focusedSelection, edges]);
}

function nodeMatchesCadenceFilter(
  data: { cadences?: string[]; kind?: string },
  cadenceFilter: ReadonlySet<string>,
): boolean {
  if (cadenceFilter.size === 0) return true;
  // Wave-group containers don't carry cadence data themselves; keep them
  // visible so children retain their parent context.
  if (data.kind === "wave-group") return true;
  const list = data.cadences ?? [];
  return list.some((c) => cadenceFilter.has(c));
}

function nodeMatchesTextSearch(data: { label?: string; kind?: string }, needle: string): boolean {
  if (needle === "") return true;
  if (data.kind === "wave-group") return true;
  const label = (data.label ?? "").toLowerCase();
  return label.includes(needle);
}

function applyTrace(
  rawNodes: Node[],
  rawEdges: Edge[],
  traceSet: Set<string> | null,
  cadenceFilter: ReadonlySet<string>,
  nodeFilter: string,
): { nodes: Node[]; edges: Edge[] } {
  const needle = nodeFilter.trim().toLowerCase();
  const nodes = rawNodes.map((n) => {
    const data = n.data as { cadences?: string[]; kind?: string; label?: string };
    const cadenceOk = nodeMatchesCadenceFilter(data, cadenceFilter);
    const textOk = nodeMatchesTextSearch(data, needle);
    return {
      ...n,
      data: {
        ...n.data,
        inTrace: traceSet?.has(n.id),
        matchesFilter: cadenceOk && textOk,
      },
    };
  });
  const edges =
    traceSet == null
      ? rawEdges
      : rawEdges.map((e) => {
          const onCascade = traceSet.has(e.source) && traceSet.has(e.target);
          return {
            ...e,
            animated: onCascade,
            // Cascade edges float above the node layer via zIndex; dashed
            // stroke makes them readable when overlapping the cards.
            zIndex: onCascade ? 1000 : 0,
            style: onCascade ? { strokeWidth: 2, strokeDasharray: "6 4" } : { opacity: 0.25 },
          };
        });
  return { nodes, edges };
}

function useEscapeClearsFocus(
  focusedSelection: TableSelection | null | undefined,
  onFocusChange: ((s: TableSelection | null) => void) | undefined,
): void {
  useEffect(() => {
    if (focusedSelection == null || onFocusChange == null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onFocusChange(null);
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, [focusedSelection, onFocusChange]);
}

function usePendingClickCleanup(pendingClickRef: React.RefObject<PendingClick | null>): void {
  useEffect(() => {
    return () => {
      if (pendingClickRef.current != null) {
        clearTimeout(pendingClickRef.current.timer);
        pendingClickRef.current = null;
      }
    };
  }, [pendingClickRef]);
}

function collectCadences(graph: PipelineGraph): string[] {
  const set = new Set<string>();
  for (const wave of graph.visibleWaves) {
    for (const node of graph.waves[wave]) {
      for (const c of node.cadences ?? []) set.add(c);
    }
  }
  // Stable ordering by known cadence first, then alphabetical for the rest.
  const known = ["hourly", "daily", "weekly", "monthly"];
  const ordered: string[] = [];
  for (const k of known) if (set.has(k)) ordered.push(k);
  const remaining = [...set].filter((c) => !known.includes(c)).sort();
  return [...ordered, ...remaining];
}

function CadenceFilterRow({
  cadences,
  cadenceFilter,
  onToggle,
  onClear,
  nodeFilter,
  onNodeFilterChange,
}: {
  cadences: string[];
  cadenceFilter: ReadonlySet<string>;
  onToggle: (cadence: string) => void;
  onClear: () => void;
  nodeFilter: string;
  onNodeFilterChange: (next: string) => void;
}) {
  const anyActive = cadenceFilter.size > 0;
  const hasCadences = cadences.length > 0;
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
      <label className="flex items-center gap-1.5">
        <span className="font-medium">Search:</span>
        <input
          type="search"
          value={nodeFilter}
          onChange={(e) => onNodeFilterChange(e.target.value)}
          placeholder="filter nodes by label"
          className="w-48 rounded border border-border bg-card px-2 py-0.5 text-[11px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Filter nodes by label"
        />
      </label>
      {hasCadences ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">Schedule:</span>
          {cadences.map((cadence) => {
            const selected = cadenceFilter.has(cadence);
            const muted = anyActive && !selected;
            return (
              <button
                key={cadence}
                type="button"
                onClick={() => onToggle(cadence)}
                aria-pressed={selected}
                className={`transition ${muted ? "opacity-40" : ""} ${selected ? "rounded ring-2 ring-primary" : ""}`}
              >
                <CategoryBadge color={cadenceColor(cadence)}>
                  {cadence.charAt(0).toUpperCase() + cadence.slice(1)}
                </CategoryBadge>
              </button>
            );
          })}
          {anyActive ? (
            <Button variant="chip" onClick={onClear}>
              Clear
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface DagCanvasProps {
  nodes: Node[];
  edges: Edge[];
  onNodeClick: NodeMouseHandler;
  onNodeDoubleClick: NodeMouseHandler;
  onPaneClick: () => void;
  onNodeContextMenu: NodeMouseHandler;
  onPaneContextMenu: (event: React.MouseEvent | MouseEvent) => void;
}

function DagCanvas({
  nodes,
  edges,
  onNodeClick,
  onNodeDoubleClick,
  onPaneClick,
  onNodeContextMenu,
  onPaneContextMenu,
}: DagCanvasProps) {
  return (
    <div
      className="relative rounded border border-border bg-card"
      // Fill remaining viewport height under the page header with a small
      // bottom margin. Grows with the browser; never below 480px.
      style={{ height: "max(480px, calc(100vh - 220px))" }}
      data-testid="pipeline-dag"
    >
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodeClick={onNodeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          onPaneClick={onPaneClick}
          onNodeContextMenu={onNodeContextMenu}
          onPaneContextMenu={onPaneContextMenu}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          // Allow zooming out far enough to fit any reasonable graph; React
          // Flow defaults minZoom to 0.5 which clips wider DAGs.
          minZoom={0.1}
          maxZoom={2}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag
          zoomOnScroll
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          {/* Dark-mode-friendly Controls: override the default white buttons
              with token-driven backgrounds + foreground colors. The
              `[&_button]` selector targets the zoom/fit buttons inside. */}
          <Controls
            showInteractive={false}
            className="!bg-card !border-border [&_button]:!bg-card [&_button]:!text-foreground [&_button]:!border-border [&_button:hover]:!bg-accent"
          />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}

interface ContextMenuState {
  x: number;
  y: number;
  selection: TableSelection | null;
}

function ContextMenu({
  menu,
  isFocused,
  onClose,
  onOpenDetails,
  onFocus,
  onClearFocus,
}: {
  menu: ContextMenuState;
  isFocused: boolean;
  onClose: () => void;
  onOpenDetails: () => void;
  onFocus: () => void;
  onClearFocus: () => void;
}) {
  useEffect(() => {
    const handler = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent && e.key !== "Escape") return;
      onClose();
    };
    window.addEventListener("mousedown", handler);
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("mousedown", handler);
      window.removeEventListener("keydown", handler);
    };
  }, [onClose]);
  return (
    <div
      role="menu"
      tabIndex={-1}
      data-testid="pipeline-context-menu"
      className="fixed z-50 min-w-[160px] overflow-hidden rounded border border-border bg-card shadow-lg"
      style={{ left: menu.x, top: menu.y }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {menu.selection != null ? (
        <>
          <Button variant="menuItem" role="menuitem" onClick={onOpenDetails}>
            Open details
          </Button>
          {isFocused ? (
            <Button variant="menuItem" role="menuitem" onClick={onClearFocus}>
              Clear focus
            </Button>
          ) : (
            <Button variant="menuItem" role="menuitem" onClick={onFocus}>
              Focus cascade
            </Button>
          )}
        </>
      ) : (
        <Button variant="menuItem" role="menuitem" disabled={!isFocused} onClick={onClearFocus}>
          Clear focus
        </Button>
      )}
    </div>
  );
}

function useNodeClickHandlers(
  pendingClickRef: React.RefObject<PendingClick | null>,
  toggleWave: (wave: Wave) => void,
  onTableSelect: ((s: TableSelection) => void) | undefined,
  onFocusChange: ((s: TableSelection | null) => void) | undefined,
): { onNodeClick: NodeMouseHandler; onNodeDoubleClick: NodeMouseHandler } {
  const onNodeClick = useCallback<NodeMouseHandler>(
    (event, node) => {
      // The "Show/Hide N inactive" button inside the wave-group header lives
      // inside the React Flow node. It stops propagation itself, but defend
      // here too in case the target check is the only signal we get.
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-action="toggle-inactive"]') != null) return;
      const data = node.data as { kind?: string; wave?: Wave };
      if (data.kind === "wave-group" && data.wave != null) {
        toggleWave(data.wave);
        return;
      }
      const selection = nodeDataAsSelection(node);
      if (selection == null) return;
      // Second click on the same node within the double-click window → drawer.
      if (pendingClickRef.current?.nodeId === node.id) {
        clearTimeout(pendingClickRef.current.timer);
        pendingClickRef.current = null;
        onTableSelect?.(selection);
        return;
      }
      if (pendingClickRef.current != null) clearTimeout(pendingClickRef.current.timer);
      const timer = setTimeout(() => {
        pendingClickRef.current = null;
        onFocusChange?.(selection);
      }, DOUBLE_CLICK_MS);
      pendingClickRef.current = { nodeId: node.id, timer, selection };
    },
    [pendingClickRef, toggleWave, onTableSelect, onFocusChange],
  );
  const onNodeDoubleClick = useCallback<NodeMouseHandler>(
    (_event, node) => {
      if (pendingClickRef.current != null) {
        clearTimeout(pendingClickRef.current.timer);
        pendingClickRef.current = null;
      }
      const selection = nodeDataAsSelection(node);
      if (selection != null) onTableSelect?.(selection);
    },
    [pendingClickRef, onTableSelect],
  );
  return { onNodeClick, onNodeDoubleClick };
}

function useCadenceFilterControls(
  setCadenceFilter: React.Dispatch<React.SetStateAction<ReadonlySet<string>>>,
): { toggleCadence: (cadence: string) => void; clearCadenceFilter: () => void } {
  const toggleCadence = useCallback(
    (cadence: string) => {
      setCadenceFilter((prev) => {
        const next = new Set(prev);
        if (next.has(cadence)) next.delete(cadence);
        else next.add(cadence);
        return next;
      });
    },
    [setCadenceFilter],
  );
  const clearCadenceFilter = useCallback(() => setCadenceFilter(new Set()), [setCadenceFilter]);
  return { toggleCadence, clearCadenceFilter };
}

interface ContextMenuApi {
  menu: ContextMenuState | null;
  isFocused: boolean;
  onNodeContextMenu: NodeMouseHandler;
  onPaneContextMenu: (event: React.MouseEvent | MouseEvent) => void;
  onClose: () => void;
  onOpenDetails: () => void;
  onFocus: () => void;
  onClearFocus: () => void;
}

function useContextMenu(
  focusedSelection: TableSelection | null | undefined,
  onTableSelect: ((s: TableSelection) => void) | undefined,
  onFocusChange: ((s: TableSelection | null) => void) | undefined,
): ContextMenuApi {
  const [menu, setMenu] = useState<ContextMenuState | null>(null);
  const onClose = useCallback(() => setMenu(null), []);
  const onNodeContextMenu = useCallback<NodeMouseHandler>((event, node) => {
    event.preventDefault();
    const selection = nodeDataAsSelection(node);
    setMenu({ x: event.clientX, y: event.clientY, selection });
  }, []);
  const onPaneContextMenu = useCallback((event: React.MouseEvent | MouseEvent) => {
    event.preventDefault();
    setMenu({ x: event.clientX, y: event.clientY, selection: null });
  }, []);
  const onOpenDetails = useCallback(() => {
    if (menu?.selection != null) onTableSelect?.(menu.selection);
    setMenu(null);
  }, [menu, onTableSelect]);
  const onFocus = useCallback(() => {
    if (menu?.selection != null) onFocusChange?.(menu.selection);
    setMenu(null);
  }, [menu, onFocusChange]);
  const onClearFocus = useCallback(() => {
    onFocusChange?.(null);
    setMenu(null);
  }, [onFocusChange]);
  return {
    menu,
    isFocused: focusedSelection != null,
    onNodeContextMenu,
    onPaneContextMenu,
    onClose,
    onOpenDetails,
    onFocus,
    onClearFocus,
  };
}

function DagInstructions() {
  return (
    <p className="mb-2 text-xs text-muted-foreground">
      Click a wave header to expand or collapse. Single-click a card to focus its upstream and
      downstream cascade; double-click to open details. Click a schedule badge to mute nodes that
      don&apos;t match that cadence. Press
      <kbd className="mx-1 rounded border border-border bg-card px-1">Esc</kbd>
      or click an empty area to clear focus.
    </p>
  );
}

export function PipelineDag({
  graph,
  onTableSelect,
  focusedSelection,
  onFocusChange,
}: PipelineDagProps) {
  const { expandedWaves, toggleWave } = useExpandedWaves(graph);
  const { showInactiveByWave, toggleShowInactive } = useShowInactiveByWave();
  const [cadenceFilter, setCadenceFilter] = useState<ReadonlySet<string>>(() => new Set());
  const [nodeFilter, setNodeFilter] = useState<string>("");

  const flow = useMemo(
    () => buildFlowElements(graph, expandedWaves, showInactiveByWave),
    [graph, expandedWaves, showInactiveByWave],
  );
  const traceSet = useTraceSet(flow.edges, focusedSelection);
  const { nodes, edges } = useMemo(
    () => applyTrace(flow.nodes, flow.edges, traceSet, cadenceFilter, nodeFilter),
    [flow.nodes, flow.edges, traceSet, cadenceFilter, nodeFilter],
  );

  const pendingClick = useRef<PendingClick | null>(null);
  usePendingClickCleanup(pendingClick);

  const { onNodeClick, onNodeDoubleClick } = useNodeClickHandlers(
    pendingClick,
    toggleWave,
    onTableSelect,
    onFocusChange,
  );

  useEscapeClearsFocus(focusedSelection, onFocusChange);

  const { toggleCadence, clearCadenceFilter } = useCadenceFilterControls(setCadenceFilter);

  const cadences = useMemo(() => collectCadences(graph), [graph]);

  const callbacks = useMemo<DagCallbacks>(() => ({ toggleShowInactive }), [toggleShowInactive]);

  const onPaneClick = useCallback(() => onFocusChange?.(null), [onFocusChange]);

  const contextMenu = useContextMenu(focusedSelection, onTableSelect, onFocusChange);

  return (
    <DagCallbacksContext.Provider value={callbacks}>
      <div className="flex flex-col gap-2">
        <DagInstructions />
        <CadenceFilterRow
          cadences={cadences}
          cadenceFilter={cadenceFilter}
          onToggle={toggleCadence}
          onClear={clearCadenceFilter}
          nodeFilter={nodeFilter}
          onNodeFilterChange={setNodeFilter}
        />
        <DagCanvas
          nodes={nodes}
          edges={edges}
          onNodeClick={onNodeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          onPaneClick={onPaneClick}
          onNodeContextMenu={contextMenu.onNodeContextMenu}
          onPaneContextMenu={contextMenu.onPaneContextMenu}
        />
        {contextMenu.menu != null ? (
          <ContextMenu
            menu={contextMenu.menu}
            isFocused={contextMenu.isFocused}
            onClose={contextMenu.onClose}
            onOpenDetails={contextMenu.onOpenDetails}
            onFocus={contextMenu.onFocus}
            onClearFocus={contextMenu.onClearFocus}
          />
        ) : null}
      </div>
    </DagCallbacksContext.Provider>
  );
}
